import { motion } from "framer-motion";
import { OEM_NAMES } from "../data/oemNames";

const oemName = (slug) => OEM_NAMES[slug] || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Industry-standard reference-architecture layout: full-width numbered layer
// bands with a visible dependency flow, per-layer descriptions, capability
// chips and the certified OEM platforms scoped to each layer.
export default function SolutionDiagram({ diagram, testId = "solution-diagram" }) {
  if (!diagram?.layers) return null;
  return (
    <div className="relative" data-testid={testId}>
      {diagram.layers.map((layer, li) => (
        <div key={layer.label} className="relative">
          {li > 0 && (
            <div className="relative h-10 md:h-12" aria-hidden="true">
              <motion.div
                className="absolute left-11 md:left-[52px] w-px h-full bg-gradient-to-b from-cy/50 via-cy/25 to-cy/10"
                initial={{ scaleY: 0, originY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.05 }}
              />
              <motion.span
                className="absolute left-11 md:left-[52px] top-1/2 -translate-x-1/2 -translate-y-1/2 h-1.5 w-1.5 rotate-45 bg-ember"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.35 }}
              />
            </div>
          )}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.65, delay: Math.min(li * 0.08, 0.32), ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-md border border-white/10 bg-[#14151A] p-6 md:p-8 hover:border-cy/40 transition-colors duration-500"
            data-testid={`${testId}-layer-${li + 1}`}
          >
            <div className="grid lg:grid-cols-[300px_1fr] gap-5 lg:gap-12">
              <div>
                <div className="flex items-start gap-4">
                  <span
                    className="font-tech font-black text-3xl md:text-4xl leading-none text-transparent select-none"
                    style={{ WebkitTextStroke: "1px rgba(0,137,161,0.65)" }}
                  >
                    {String(li + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display font-bold text-white text-lg md:text-xl leading-tight pt-0.5">{layer.label}</h3>
                </div>
                {layer.desc && <p className="mt-4 text-sm text-white/50 leading-relaxed">{layer.desc}</p>}
              </div>
              <div>
                <div className="flex flex-wrap gap-2">
                  {layer.items.map((item) => (
                    <span key={item} className="rounded-sm border border-white/15 bg-white/5 text-white text-xs md:text-sm font-bold px-3.5 py-2.5 hover:border-ember/60 hover:bg-white/[0.08] transition-colors duration-300">
                      {item}
                    </span>
                  ))}
                </div>
                {layer.oems?.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-white/10">
                    <p className="text-[9px] font-tech tracking-[0.28em] text-white/35 mb-3">KEY PLATFORMS</p>
                    <div className="flex flex-wrap gap-2">
                      {layer.oems.map((slug) => (
                        <span key={slug} className="inline-flex items-center gap-2 bg-white rounded-sm px-2.5 py-1.5" data-testid={`${testId}-layer-${li + 1}-oem-${slug}`}>
                          <img src={`/logos/oem/${slug}.png`} alt={oemName(slug)} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-4 w-4 object-contain" />
                          <span className="text-[10px] font-tech font-bold text-[#101828] whitespace-nowrap">{oemName(slug)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      ))}
    </div>
  );
}
